// ============================================================================
// Reopen Pre-Litigation Case Modal
// ============================================================================
// Supervisor reopens a closed early-recovery case; reason is required
// (10..2000 chars). Backend clears the CLOSED closure row and reactivates
// the workflow.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useReopenPreLitCase } from 'hooks/api/usePreLitigation';
import type { ClosedCaseReviewRow } from 'api/types/preLitigation.types';

interface Props {
  isOpen: boolean;
  row: ClosedCaseReviewRow | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ReopenPreLitCaseModal: React.FC<Props> = ({ isOpen, row, onClose, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const { run, loading } = useReopenPreLitCase();

  useEffect(() => {
    if (isOpen) { setReason(''); setError(null); }
  }, [isOpen]);

  const handleSaveClick = useCallback(() => {
    const trimmed = reason.trim();
    if (trimmed.length < 10) {
      setError('Please explain the reason for reopening (min. 10 characters).');
      return;
    }
    setError(null);
    setSaveConfirmOpen(true);
  }, [reason]);

  const handleConfirmSave = useCallback(async () => {
    if (!row) return;
    setSaveConfirmOpen(false);
    try {
      await run(row.id, { reopenedReason: reason.trim() });
      notificationService.toast.success(`Case ${row.caseCode} reopened.`);
      onSuccess();
      onClose();
    } catch {
      notificationService.toast.error('Failed to reopen case. Please try again.');
    }
  }, [row, reason, run, onSuccess, onClose]);

  if (!isOpen || !row) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-md">
        {loading && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#dc2626', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Reopening...</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Reopen Case</h2>
          <button className="modal-close" onClick={onClose} disabled={loading}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          <div style={{
            padding: '0.75rem 1rem',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
            marginBottom: '1rem',
            fontSize: '0.85rem',
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <div><strong>Case:</strong> {row.caseCode}</div>
              <div><strong>Customer:</strong> {row.customerName}</div>
              <div><strong>Account:</strong> {row.accountNumber}</div>
              <div><strong>Closed on:</strong> {row.closedAt ? new Date(row.closedAt).toLocaleDateString('en-IN') : '—'}</div>
              <div style={{ gridColumn: '1 / -1' }}>
                <strong>Original closure:</strong> {row.closureReasonName}
              </div>
            </div>
          </div>

          <div className="form-group" data-field="reopenedReason">
            <label className="form-label">Reason for Reopening <span className="text-danger">*</span></label>
            <textarea
              className={`form-textarea${error ? ' form-field-error' : ''}`}
              rows={5}
              value={reason}
              maxLength={2000}
              placeholder="Explain why this closed case should be reopened (min. 10 characters)..."
              onChange={(e) => { setReason(e.target.value); if (error) setError(null); }}
            />
            {error && <div className="form-error-message">{error}</div>}
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', textAlign: 'right', marginTop: '0.25rem' }}>
              {reason.length}/2000
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn btn-danger" onClick={handleSaveClick} disabled={loading}>
            Reopen Case
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Reopen Case"
        message={`Reopen case ${row.caseCode}? This will reactivate the workflow.`}
        confirmLabel="Reopen"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />
    </>
  );
};

export default ReopenPreLitCaseModal;
